import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import SubMenuBar from './SubMenuBar';
import './Metronome.css';
import { getItem, setItem } from '../utils/storage';
import { trackFeatureUsage } from '../utils/analytics';

const METRONOME_PREFS_KEY = 'metronome_preferences';

const Metronome = forwardRef((props, ref) => {
  const [playing, setPlaying] = useState(false);
  const [bpm, setBpm] = useState(100);
  const [selectedView, setSelectedView] = useState('Pendulum'); // Default to Pendulum
  const [selectedSound, setSelectedSound] = useState('Beep'); // Default to Beep
  const [hapticsEnabled, setHapticsEnabled] = useState(false); // Default to Off
  const [hapticsInitialized, setHapticsInitialized] = useState(false); // Track if haptics have been initialized with user gesture
  const audioContextRef = useRef(null);
  const nextNoteTimeRef = useRef(0);
  const timerIDRef = useRef(null);
  const scheduleAheadTime = 0.1; // How far ahead to schedule audio (seconds)
  const lookaheadInterval = 25.0; // How frequently to call scheduling function (milliseconds)



  // Load preferences from storage on mount
  useEffect(() => {
    const prefs = getItem(METRONOME_PREFS_KEY, { 
      view: 'Pendulum', 
      sound: 'Beep', 
      haptics: false 
    });
    setSelectedView(prefs.view);
    setSelectedSound(prefs.sound);
    setHapticsEnabled(prefs.haptics);
    

    
    // Track that the metronome component was loaded
    trackFeatureUsage('metronome', 'load');
  }, []);

  // Menu configuration for SubMenuBar
  const menus = {
    view: {
      label: 'View',
      items: ['None', 'Circle', 'Pendulum'],
      selected: selectedView,
      onSelect: value => {
        // Initialize haptics on user interaction
        initializeHapticsOnGesture();
        
        setSelectedView(value);
        setItem(METRONOME_PREFS_KEY, { 
          view: value, 
          sound: selectedSound, 
          haptics: hapticsEnabled 
        });
        trackFeatureUsage('metronome_view', value);
      },
    },
    sound: {
      label: 'Sound',
      items: ['None', 'Beep', 'Snare Drum', 'Bass Drum'],
      selected: selectedSound,
      onSelect: value => {
        // Initialize haptics on user interaction
        initializeHapticsOnGesture();
        
        setSelectedSound(value);
        setItem(METRONOME_PREFS_KEY, { 
          view: selectedView, 
          sound: value, 
          haptics: hapticsEnabled 
        });
        trackFeatureUsage('metronome_sound', value);
      },
    },
    haptics: {
      label: 'Haptics',
      items: ['On', 'Off'],
      selected: hapticsEnabled ? 'On' : 'Off',
      onSelect: value => {
        const enabled = value === 'On';
        
        // Show warning for Safari when enabling haptics
        if (enabled && /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)) {
          const userConfirmed = window.confirm(
            'Safari does not support haptic feedback for web applications. ' +
            'Haptics will not work in Safari. ' +
            'For haptic feedback, please use Chrome or another browser that supports the Vibration API.\n\n' +
            'Do you still want to enable haptics?'
          );
          
          if (!userConfirmed) {
            return; // Don't enable haptics if user cancels
          }
        }
        
        // Initialize haptics on user interaction
        if (enabled) {
          initializeHapticsOnGesture();
        }
        
        setHapticsEnabled(enabled);
        setItem(METRONOME_PREFS_KEY, { 
          view: selectedView, 
          sound: selectedSound, 
          haptics: enabled 
        });
        trackFeatureUsage('metronome_haptics', value);
      },
    },
  };

  // Constants for consistent tone generation
  const TONE_FREQUENCY = 880; // A5 note
  const TONE_LENGTH = 0.05; // 50ms duration
  const TONE_VOLUME = 0.5; // 50% volume

  // Audio buffer cache for drum sounds
  const audioBuffersRef = useRef({});

  // Load drum sound
  const loadDrumSound = async type => {
    if (audioBuffersRef.current[type]) return;

    try {
      const response = await fetch(`/sounds/${type.toLowerCase().replace(' ', '_')}.wav`);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      audioBuffersRef.current[type] = audioBuffer;
    } catch (error) {
      console.error(`Error loading ${type} sound:`, error);
    }
  };

  const stopMetronome = () => {
    if (playing) {
      clearTimeout(timerIDRef.current);
      setPlaying(false);
    }
  };

  // Expose the stop function to parent components
  useImperativeHandle(ref, () => ({
    stop: stopMetronome,
  }));

  const nextNote = () => {
    const secondsPerBeat = 60.0 / bpm;
    nextNoteTimeRef.current += secondsPerBeat;
  };

  const scheduleNote = beatTime => {
    // Trigger haptic feedback if enabled and supported
    if (hapticsEnabled && hapticsInitialized) {
      // Schedule haptic feedback to happen at the same time as the audio
      const currentTime = audioContextRef.current.currentTime;
      const delay = Math.max(0, (beatTime - currentTime) * 1000); // Convert to milliseconds
      
      setTimeout(() => {
        triggerHaptic();
      }, delay);
    }

    if (selectedSound === 'None') return;

    const audioContext = audioContextRef.current;

    if (selectedSound === 'Beep') {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(TONE_FREQUENCY, beatTime);

      gainNode.gain.setValueAtTime(0, beatTime);
      gainNode.gain.linearRampToValueAtTime(TONE_VOLUME, beatTime + 0.005);
      gainNode.gain.setValueAtTime(TONE_VOLUME, beatTime + 0.005);
      gainNode.gain.linearRampToValueAtTime(0, beatTime + TONE_LENGTH);

      oscillator.start(beatTime);
      oscillator.stop(beatTime + TONE_LENGTH);
    } else if (selectedSound === 'Snare Drum') {
      // Create noise source for snare
      const bufferSize = audioContext.sampleRate * 0.05; // 50ms buffer
      const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }

      const noise = audioContext.createBufferSource();
      noise.buffer = buffer;

      // Bandpass filter for snare character
      const filter = audioContext.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 2000;
      filter.Q.value = 1.5;

      // Envelope for snare
      const gainNode = audioContext.createGain();
      gainNode.gain.setValueAtTime(0, beatTime);
      gainNode.gain.linearRampToValueAtTime(TONE_VOLUME, beatTime + 0.001);
      gainNode.gain.exponentialRampToValueAtTime(0.01, beatTime + 0.1);
      gainNode.gain.linearRampToValueAtTime(0, beatTime + 0.11);

      noise.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(audioContext.destination);

      noise.start(beatTime);
      noise.stop(beatTime + 0.11);
    } else if (selectedSound === 'Bass Drum') {
      // Main oscillator for fundamental tone
      const osc = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      // Second oscillator for body resonance
      const osc2 = audioContext.createOscillator();
      const gain2 = audioContext.createGain();

      // Third oscillator for attack character
      const osc3 = audioContext.createOscillator();
      const gain3 = audioContext.createGain();

      // Low-pass filter for warmth
      const filter = audioContext.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(120, beatTime);
      filter.Q.value = 2.0;

      // Distortion for mallet/drumhead interaction
      const distortion = audioContext.createWaveShaper();
      function makeDistortionCurve(amount) {
        const k = amount;
        const n_samples = 44100;
        const curve = new Float32Array(n_samples);
        for (let i = 0; i < n_samples; ++i) {
          const x = (i * 2) / n_samples - 1;
          curve[i] = ((Math.PI + k) * x) / (Math.PI + k * Math.abs(x));
        }
        return curve;
      }
      distortion.curve = makeDistortionCurve(5);

      // Set oscillator types
      osc.type = 'sine'; // Deep fundamental
      osc2.type = 'triangle'; // Body resonance
      osc3.type = 'sine'; // Attack transient

      // Pitch envelopes
      osc.frequency.setValueAtTime(55, beatTime); // Fundamental at A1
      osc.frequency.exponentialRampToValueAtTime(35, beatTime + 0.1);

      osc2.frequency.setValueAtTime(110, beatTime); // First harmonic
      osc2.frequency.exponentialRampToValueAtTime(55, beatTime + 0.15);

      osc3.frequency.setValueAtTime(220, beatTime); // Attack frequency
      osc3.frequency.exponentialRampToValueAtTime(55, beatTime + 0.05);

      // Amplitude envelopes
      gainNode.gain.setValueAtTime(0, beatTime);
      gainNode.gain.linearRampToValueAtTime(TONE_VOLUME * 3, beatTime + 0.005);
      gainNode.gain.exponentialRampToValueAtTime(TONE_VOLUME * 1.5, beatTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(TONE_VOLUME * 0.8, beatTime + 0.3);
      gainNode.gain.exponentialRampToValueAtTime(0.01, beatTime + 0.8);
      gainNode.gain.linearRampToValueAtTime(0, beatTime + 0.81);

      gain2.gain.setValueAtTime(0, beatTime);
      gain2.gain.linearRampToValueAtTime(TONE_VOLUME * 1.5, beatTime + 0.001);
      gain2.gain.exponentialRampToValueAtTime(TONE_VOLUME * 0.5, beatTime + 0.2);
      gain2.gain.exponentialRampToValueAtTime(0.01, beatTime + 0.6);
      gain2.gain.linearRampToValueAtTime(0, beatTime + 0.61);

      gain3.gain.setValueAtTime(0, beatTime);
      gain3.gain.linearRampToValueAtTime(TONE_VOLUME * 2, beatTime + 0.001);
      gain3.gain.exponentialRampToValueAtTime(0.01, beatTime + 0.05);
      gain3.gain.linearRampToValueAtTime(0, beatTime + 0.06);

      // Connect the signal chain
      osc.connect(gainNode);
      osc2.connect(gain2);
      osc3.connect(gain3);

      gainNode.connect(filter);
      gain2.connect(filter);
      gain3.connect(distortion);

      filter.connect(distortion);
      distortion.connect(audioContext.destination);

      // Start and stop oscillators
      osc.start(beatTime);
      osc2.start(beatTime);
      osc3.start(beatTime);
      osc.stop(beatTime + 0.81);
      osc2.stop(beatTime + 0.61);
      osc3.stop(beatTime + 0.06);
    }
  };

  // Load drum sounds when sound type changes
  useEffect(() => {
    if (!audioContextRef.current) return;

    if (['Snare Drum', 'Bass Drum'].includes(selectedSound)) {
      loadDrumSound(selectedSound);
    }
  }, [selectedSound]);

  const scheduler = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    const audioContext = audioContextRef.current;

    while (nextNoteTimeRef.current < audioContext.currentTime + scheduleAheadTime) {
      scheduleNote(nextNoteTimeRef.current);
      nextNote();
    }
    timerIDRef.current = setTimeout(scheduler, lookaheadInterval);
  };

  // Initialize haptics on any user interaction
  const initializeHapticsOnGesture = () => {
    if (hapticsEnabled && !hapticsInitialized) {
      const testResult = triggerHaptic();
      if (testResult) {
        setHapticsInitialized(true);
      }
    }
  };

  const startStop = () => {
    if (playing) {
      stopMetronome();
      trackFeatureUsage('metronome', 'stop');
    } else {
      // Initialize haptics FIRST, before starting metronome
      if (hapticsEnabled && !hapticsInitialized) {
        initializeHapticsOnGesture();
        
        // Wait a moment for initialization to complete
        setTimeout(() => {
          startMetronomeAfterHaptics();
        }, 100);
        return;
      }
      
      startMetronomeAfterHaptics();
    }
  };

  const startMetronomeAfterHaptics = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      nextNoteTimeRef.current = audioContextRef.current.currentTime;
      scheduler();
      setPlaying(true);
      trackFeatureUsage('metronome', 'start');
  };

  const handleBpmChange = e => {
    // Initialize haptics on user interaction
    initializeHapticsOnGesture();
    
    const newBpm = parseInt(e.target.value);
    updateBpm(newBpm);
  };

  const adjustBpm = amount => {
    // Initialize haptics on user interaction
    initializeHapticsOnGesture();
    
    const newBpm = Math.min(Math.max(bpm + amount, 60), 240);
    updateBpm(newBpm);
  };

  const updateBpm = newBpm => {
    setBpm(newBpm);
  };

  // iOS-specific haptic feedback
  const triggerIOSHaptic = () => {
    // iOS haptic feedback using AudioContext and short audio burst
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    try {
      // Resume audio context if suspended (required for iOS)
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      
      // Create a very short, inaudible audio burst that triggers iOS haptic feedback
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      // Short, audible burst to trigger iOS haptics - increased volume
      oscillator.frequency.setValueAtTime(80, audioContextRef.current.currentTime); // Slightly higher frequency
      gainNode.gain.setValueAtTime(0.1, audioContextRef.current.currentTime); // 10x louder - audible but not jarring
      gainNode.gain.linearRampToValueAtTime(0, audioContextRef.current.currentTime + 0.03); // 30ms duration
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + 0.03);
      
      return true;
    } catch (error) {
      console.error('[Metronome] iOS haptic feedback failed:', error);
      return false;
    }
  };

  // Universal haptic function that works on both Android and iOS
  const triggerHaptic = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      // Use iOS-specific haptic method
      return triggerIOSHaptic();
    } else if (navigator.vibrate) {
      // Use standard Vibration API for Android
      try {
        const result = navigator.vibrate(50);
        return result;
      } catch (error) {
        console.error('[Metronome] Android vibration failed:', error);
        return false;
      }
    }
    return false;
  };



  return (
    <div className="app-theme metronome">
      <SubMenuBar menus={menus} />
      <div className="visualization-container">
        {selectedView === 'Circle' && (
          <div className={`circle-visual ${playing ? 'playing' : ''}`} style={{ '--bpm': bpm }}>
            <div className="circle-dot"></div>
          </div>
        )}
        {selectedView === 'Pendulum' && (
          <div className={`pendulum-visual ${playing ? 'playing' : ''}`} style={{ '--bpm': bpm }}>
            <div className="pendulum-arm">
              <div className="pendulum-weight"></div>
            </div>
          </div>
        )}
      </div>
      <div className="bpm-slider">
        <div className="bpm-display">
          <button
            type="button"
            className="bpm-adjust"
            onClick={() => adjustBpm(-1)}
            aria-label="Decrease tempo"
          >
            −
          </button>
          <div className="bpm-value">{bpm} BPM</div>
          <button
            type="button"
            className="bpm-adjust"
            onClick={() => adjustBpm(1)}
            aria-label="Increase tempo"
          >
            +
          </button>
        </div>
        <input type="range" min="60" max="240" value={bpm} onChange={handleBpmChange} />
      </div>
      <button className="start-stop" onClick={startStop}>
        {playing ? 'Stop' : 'Start'}
      </button>
      

    </div>
  );
});

export default Metronome;
